import { Component, computed, inject, input } from '@angular/core';
import { RxIconModule } from '@nrwl/rx-icon';
import { LaserTreatmentControlDisplay } from './laser-treatment-control-display';
import { rxResource } from '@angular/core/rxjs-interop';
import { LaserService } from './laser-service';
import { LaserTreatmentItem } from './laser-treatment-item';
import { EmrReadonlyNotice } from '../../../helpers/components/emr-readonly-notice';
import { RX_APP_CONTEXT, RxAppContext } from '@nrwl/rx-root-state';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

/**
 * Preview-only view for the Laser widget shown in non-editing contexts.
 */
@Component({
  selector: 'fb-laser-widget-preview',
  imports: [LaserTreatmentControlDisplay, EmrReadonlyNotice, RxIconModule],
  templateUrl: './laser-widget-preview.html',
  styleUrl: './laser-widget-preview.scss',
})
export class LaserWidgetPreview {
  private readonly appContext = inject(RX_APP_CONTEXT);
  private readonly laserService = inject(LaserService);

  /**
   * Laser data to be displayed.
   */
  public readonly data = input<readonly LaserTreatmentItem[]>([]);

  /**
   * Whether the entity has `completed` status.
   */
  public readonly isCompleted = input.required();

  /**
   * Whether the entity is in readonly mode.
   */
  public readonly isEmrReadonly = input.required();

  /**
   * Company details.
   */
  protected readonly company = rxResource({
    request: () => {
      const isCompleted = this.isCompleted();
      const isEmrReadonly = this.isEmrReadonly();

      // Use historic data if the form is completed or widget is in readonly mode
      if (isCompleted || isEmrReadonly || this.isRxStaffContext) {
        return undefined;
      }

      return {};
    },
    loader: () => this.laserService.getCompany(),
  });

  /**
   * Records to be displayed.
   */
  public readonly records = computed(() => {
    const data = this.data();

    if (data.length === 0) {
      return [
        {
          item: {
            date: null,
            equipment: null,
            procedureType: null,
            smartCool: null,
            joules: null,
            repetitionRate: null,
            serviceTime: null,
            pulse: null,
            note: '',
            firstLaserOption: null,
            secondLaserOption: null,
          },
          labels: {
            smartCoolText: this.company.hasValue() ? this.company.value().smartCoolText : null,
            joulesText: this.company.hasValue() ? this.company.value().joulesText : null,
            pulseText: this.company.hasValue() ? this.company.value().pulseText : null,
            repetitionRateText: this.company.hasValue()
              ? this.company.value().repetitionRateText
              : null,
            firstLaserOptionText: this.company.hasValue()
              ? this.company.value().firstLaserOptionText
              : null,
            secondLaserOptionText: this.company.hasValue()
              ? this.company.value().secondLaserOptionText
              : null,
          },
          markerTarget: null,
        },
      ];
    }

    return this.data().map((treatment) => ({
      item: {
        date: treatment.date,
        equipment: treatment.equipmentName,
        procedureType: treatment.procedureTypeName,
        smartCool: treatment.smartCool,
        joules: treatment.joules,
        repetitionRate: treatment.repetitionRate,
        serviceTime: treatment.serviceTime,
        pulse: treatment.pulse,
        note: treatment.note,
        firstLaserOption: treatment.firstLaserOptionName,
        secondLaserOption: treatment.secondLaserOptionName,
      },
      labels: {
        smartCoolText: this.company.hasValue()
          ? this.company.value().smartCoolText
          : treatment.labels.smartCoolText,
        joulesText: this.company.hasValue()
          ? this.company.value().joulesText
          : treatment.labels.joulesText,
        pulseText: this.company.hasValue()
          ? this.company.value().pulseText
          : treatment.labels.pulseText,
        repetitionRateText: this.company.hasValue()
          ? this.company.value().repetitionRateText
          : treatment.labels.repetitionRateText,
        firstLaserOptionText: this.company.hasValue()
          ? this.company.value().firstLaserOptionText
          : treatment.labels.firstLaserOptionText,
        secondLaserOptionText: this.company.hasValue()
          ? this.company.value().secondLaserOptionText
          : treatment.labels.secondLaserOptionText,
      },
      markerTarget: treatment as unknown as ScribeSuggestionMarkerTarget,
    }));
  });

  private get isRxStaffContext(): boolean {
    return this.appContext === RxAppContext.RxStaff;
  }
}
